import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, CardContent, Box, Typography, Grid, LinearProgress, Chip,
  Collapse, Button, Divider, Tooltip as MuiTooltip
} from '@mui/material';
import {
  Insights, ExpandMore, ExpandLess, TrendingUp, Schedule, WarningAmber
} from '@mui/icons-material';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid
} from 'recharts';
import api from '../../services/api';
import { brand, status as statusColors, text } from '../../theme/colors';

const fmt = (n, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(Number(n) || 0);

const StatTile = ({ label, value, color, hint }) => (
  <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50', height: '100%' }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <MuiTooltip title={hint || ''} arrow disableHoverListener={!hint}>
      <Typography variant="h6" sx={{ fontWeight: 700, color }}>{value}</Typography>
    </MuiTooltip>
  </Box>
);

/**
 * ProjectAnalyticsPanel
 * Budget vs actual, funds flow, and timeline health for a single project.
 * Funds data comes from /api/projects/:id/money-trail (full reconciliation);
 * milestone data is passed in from the page (already loaded there).
 */
const ProjectAnalyticsPanel = ({ projectId, project, milestones = [] }) => {
  const [trail, setTrail] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const currency = project?.currency || 'USD';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(`/projects/${projectId}/money-trail`);
        if (!cancelled && res.data?.success) setTrail(res.data.data);
      } catch (err) {
        // Money trail is optional context — the milestone analytics below still render
        if (!cancelled) setTrail(null);
      }
    };
    if (projectId) load();
    return () => { cancelled = true; };
  }, [projectId]);

  const summary = trail?.summary;

  const budgetChartData = useMemo(() => milestones.map((m) => ({
    name: (m.title || m.name || 'Milestone').length > 18
      ? `${(m.title || m.name).slice(0, 16)}…`
      : (m.title || m.name || 'Milestone'),
    Budget: Number(m.budget_allocation) || 0,
    Actual: Number(m.actual_cost) || 0,
  })), [milestones]);

  const hasActuals = budgetChartData.some((d) => d.Actual > 0);

  const timeline = useMemo(() => {
    const today = new Date();
    const rows = milestones.map((m) => {
      const planned = m.planned_end_date ? new Date(m.planned_end_date) : null;
      const done = ['completed', 'verified', 'paid', 'approved'].includes(m.status);
      const overdue = !done && planned && planned < today;
      return {
        id: m.id,
        name: m.title || m.name || 'Milestone',
        progress: Math.min(100, Math.max(0, Number(m.progress) || 0)),
        planned,
        done,
        overdue,
        status: m.status,
      };
    });
    return {
      rows,
      done: rows.filter((r) => r.done).length,
      overdue: rows.filter((r) => r.overdue).length,
      active: rows.filter((r) => !r.done && !r.overdue).length,
    };
  }, [milestones]);

  // Nothing meaningful to show yet
  if (!summary && milestones.length === 0) return null;

  const funded = Number(summary?.totalFunded) || 0;
  const released = Number(summary?.totalReleased) || 0;
  const heldMaterials = Number(summary?.heldForMaterials) || 0;
  const available = Number(summary?.availableBalance) || 0;
  const pct = (n) => (funded > 0 ? Math.min(100, (n / funded) * 100) : 0);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Insights sx={{ color: brand.primary }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Project Analytics</Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
            sx={{ textTransform: 'none' }}
          >
            {expanded ? 'Hide' : 'Show'}
          </Button>
        </Box>

        <Collapse in={expanded}>
          {/* Funds flow */}
          {summary && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUp fontSize="small" sx={{ color: brand.primary }} /> Funds Flow
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={6} sm={3}>
                  <StatTile label="Funded" value={fmt(funded, currency)} color={text.primary} hint="Total paid into this project's wallet" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile label="Released" value={fmt(released, currency)} color={statusColors.success} hint="Paid out for completed milestones and materials" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile label="Held for Materials" value={fmt(heldMaterials, currency)} color={statusColors.warning} hint="Reserved for pending material orders" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatTile label="Available" value={fmt(available, currency)} color={brand.primary} hint="Remaining balance in the wallet" />
                </Grid>
              </Grid>

              {funded > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: 'flex', height: 10, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.200' }}>
                    <Box sx={{ width: `${pct(released)}%`, bgcolor: statusColors.success }} />
                    <Box sx={{ width: `${pct(heldMaterials)}%`, bgcolor: statusColors.warning }} />
                    <Box sx={{ width: `${pct(available)}%`, bgcolor: brand.primary }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                    {[['Released', statusColors.success], ['Held', statusColors.warning], ['Available', brand.primary]].map(([label, color]) => (
                      <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
                        <Typography variant="caption" color="text.secondary">{label}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {Number(summary.disputedAmount) > 0 && (
                <Typography variant="caption" sx={{ color: statusColors.error, display: 'block', mt: 1 }}>
                  {fmt(summary.disputedAmount, currency)} is currently under dispute.
                </Typography>
              )}
            </Box>
          )}

          {/* Budget vs actual */}
          {budgetChartData.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Budget vs Actual by Milestone
              </Typography>
              {!hasActuals && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Actual costs appear here as contractors report spending against each milestone.
                </Typography>
              )}
              <Box sx={{ width: '100%', height: Math.max(220, budgetChartData.length * 44) }}>
                <ResponsiveContainer>
                  <BarChart data={budgetChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tickFormatter={(v) => fmt(v, currency)} tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => fmt(v, currency)} />
                    <Legend />
                    <Bar dataKey="Budget" fill={brand.primary} radius={[0, 4, 4, 0]} barSize={12} />
                    <Bar dataKey="Actual" fill={brand.secondaryLight} radius={[0, 4, 4, 0]} barSize={12} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )}

          {/* Timeline health */}
          {timeline.rows.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Schedule fontSize="small" sx={{ color: brand.primary }} /> Timeline
                </Typography>
                <Chip size="small" label={`${timeline.done} completed`} sx={{ bgcolor: statusColors.successBg, color: statusColors.successDark }} />
                <Chip size="small" label={`${timeline.active} on track`} sx={{ bgcolor: statusColors.infoBg, color: statusColors.infoDark }} />
                {timeline.overdue > 0 && (
                  <Chip
                    size="small"
                    icon={<WarningAmber sx={{ fontSize: 14 }} />}
                    label={`${timeline.overdue} overdue`}
                    sx={{ bgcolor: statusColors.errorBg, color: statusColors.errorDark }}
                  />
                )}
              </Box>
              {timeline.rows.map((r) => (
                <Box key={r.id} sx={{ mb: 1.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.name}</Typography>
                    <Typography variant="caption" color={r.overdue ? statusColors.error : 'text.secondary'}>
                      {r.overdue
                        ? `Overdue — was due ${r.planned.toLocaleDateString()}`
                        : r.planned
                          ? `Due ${r.planned.toLocaleDateString()}`
                          : ''}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={r.done ? 100 : r.progress}
                    sx={{
                      height: 6,
                      borderRadius: 1,
                      mt: 0.25,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: r.done ? statusColors.success : (r.overdue ? statusColors.error : brand.primary)
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ProjectAnalyticsPanel;
